import React, { useState, useCallback, useEffect, useRef } from "react";
import PhoneIcon from "@mui/icons-material/Phone";
import MailIcon from "@mui/icons-material/Mail";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  getAdditionalUserInfo,
  UserCredential,
  ConfirmationResult,
} from "firebase/auth";
import { Link } from "react-router-dom";
import { auth } from "../../../firebase";
import { phoneSignInSendSMS, phoneSignInConfirm } from "../../../utils/auth";
import { signup } from "../../../api";
import PhoneInput from "../PhoneInput";
import { DialCode } from "../../../utils/dialCode";
import {
  AccountConflict,
  SignInMethod,
  getAccountConflict,
} from "../../../utils/accountConflict";
import styles from "./AuthForm.module.scss";

export interface AuthFormProps {
  /**
   * 'signup'  – always creates a new account.
   * 'signin'  – always signs into an existing account.
   * 'auto'    – tries sign-in first; if no account exists, creates one.
   */
  mode: "signin" | "signup" | "auto";
  onSuccess: () => void;
  defaultEmail?: string;
  /** Full E.164 number already collected upstream — prefilled, not re-asked. */
  defaultPhone?: string;
  /** Dial code the phone field should start on (see PhoneInput). */
  defaultDialCode?: DialCode;
  /**
   * Name already collected upstream. When both parts are known the "what
   * should we call you?" step is skipped — the caller writes the name itself.
   */
  defaultFirstName?: string;
  defaultLastName?: string;
  showFooter?: boolean;
  /** When true, renders a dynamic title + subtitle above the form */
  showHeader?: boolean;
  /** Hidden during OTP entry */
  below?: React.ReactNode;
  /**
   * Opens on this sign-in tab with this message — set after signup found the
   * customer's profile under a different login (see utils/accountConflict).
   */
  signInPrompt?: AccountConflict | null;
}

type Step = "auth" | "name";

/** Seconds before "Resend code" unlocks, so a slow SMS isn't double-sent. */
const RESEND_COOLDOWN_S = 30;

const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  onSuccess,
  defaultEmail = "",
  defaultPhone = "",
  defaultDialCode,
  defaultFirstName = "",
  defaultLastName = "",
  showFooter = false,
  showHeader = false,
  below,
  signInPrompt,
}) => {
  // A name supplied by the caller is already being saved by it, so the name
  // step here would just ask the user for something they've typed once.
  const nameKnown = !!defaultFirstName.trim() && !!defaultLastName.trim();

  const [step, setStep] = useState<Step>("auth");
  // Start on whichever method we already have a value for, so nothing the
  // user has entered gets asked for a second time.
  const [method, setMethod] = useState<SignInMethod>(
    signInPrompt?.signInWith ??
      (!defaultPhone && defaultEmail ? "email" : "phone"),
  );

  // phone
  const [fullPhone, setFullPhone] = useState(defaultPhone);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  // A deadline, not a tick count: the tab is backgrounded (timers throttled)
  // exactly while the customer reads the SMS, so a count would freeze.
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now);
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  // email
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");

  // name collection
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [nameLoading, setNameLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(signInPrompt?.message ?? "");

  // Where each send builds its invisible reCAPTCHA (see phoneSignInSendSMS).
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  // The code session for this form's last send; a resend replaces it.
  const confirmationRef = useRef<ConfirmationResult | null>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(t);
  }, [resendIn, now]);

  const resetOtp = useCallback(() => {
    setError("");
    setOtpSent(false);
    setOtp("");
    setResendAt(0);
  }, []);

  const switchMethod = useCallback(
    (m: SignInMethod) => {
      setMethod(m);
      resetOtp();
    },
    [resetOtp],
  );

  // After Firebase auth — check if the user is new; if so collect their name
  const handleAuthResult = useCallback(
    async (credential: UserCredential | null | undefined) => {
      const isNew = credential
        ? (getAdditionalUserInfo(credential)?.isNewUser ?? false)
        : false;

      if (isNew && !nameKnown) {
        // The sign-in step is done; its spinner must not survive a return to
        // it (the account-conflict path sends the customer back there).
        setLoading(false);
        setStep("name");
      } else {
        onSuccess();
      }
    },
    [onSuccess, nameKnown],
  );

  const handleNameSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    setError("");
    setNameLoading(true);
    try {
      await signup({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(method === "phone"
          ? { phone: fullPhone }
          : { email: auth.currentUser?.email }),
      });
      onSuccess();
    } catch (e: any) {
      const conflict = getAccountConflict(e);
      if (conflict) {
        // Their profile is on another login (api.signup signed the stray one
        // out): back to sign in, the way the server says.
        setStep("auth");
        switchMethod(conflict.signInWith);
        setNotice(conflict.message);
        return;
      }
      const code = e?.code;
      setError(
        `Couldn't save your name. Please try again.${code ? ` (${code})` : ""}`,
      );
    } finally {
      setNameLoading(false);
    }
  };

  // ── Phone flow ─────────────────────────────────────────────────────────────

  const handleSendCode = async () => {
    if (!fullPhone.trim()) {
      setError("Please enter your phone number.");
      return;
    }
    setError("");
    setSending(true);
    try {
      confirmationRef.current = await phoneSignInSendSMS(
        fullPhone.trim(),
        recaptchaContainerRef.current,
      );
      // Also the resend path: a new code supersedes the old one.
      setOtpSent(true);
      setOtp("");
      setResendAt(Date.now() + RESEND_COOLDOWN_S * 1000);
      setNow(Date.now());
    } catch (e: any) {
      console.error("Phone code send failed", {
        phone: fullPhone.trim(),
        resend: otpSent,
        code: e?.code,
        message: e?.message,
      });
      setError(
        authError(
          PHONE_SEND_ERRORS,
          e?.code,
          "Couldn't send the code. Please try again.",
        ),
      );
    } finally {
      setSending(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (otp.trim().length < 4) {
      setError("Please enter the full verification code.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const result = await phoneSignInConfirm(
        confirmationRef.current,
        otp.trim(),
      );
      await handleAuthResult(result ?? null);
    } catch (e: any) {
      setLoading(false);
      setError(
        authError(
          PHONE_CONFIRM_ERRORS,
          e?.code,
          "Something went wrong. Please try again.",
        ),
      );
    }
  };

  // ── Email flow ─────────────────────────────────────────────────────────────

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      let result: UserCredential;
      if (mode === "signup") {
        result = await createUserWithEmailAndPassword(
          auth,
          email.trim(),
          password,
        );
      } else if (mode === "auto") {
        try {
          result = await signInWithEmailAndPassword(
            auth,
            email.trim(),
            password,
          );
        } catch (e: any) {
          if (
            e?.code === "auth/user-not-found" ||
            e?.code === "auth/invalid-credential"
          ) {
            result = await createUserWithEmailAndPassword(
              auth,
              email.trim(),
              password,
            );
          } else {
            throw e;
          }
        }
      } else {
        result = await signInWithEmailAndPassword(auth, email.trim(), password);
      }
      await handleAuthResult(result);
    } catch (e: any) {
      setLoading(false);
      const fallback =
        mode === "signup"
          ? "Couldn't create your account. Please try again."
          : "Sign in failed. Please try again.";
      setError(authError(EMAIL_ERRORS, e?.code, fallback));
    }
  };

  // ── Name collection step ───────────────────────────────────────────────────

  if (step === "name") {
    return (
      <>
        {showHeader ? (
          <div className={styles.Header}>
            <h2 className={styles.HeaderTitle}>One last thing!</h2>
            <p className={styles.HeaderSubtitle}>What should we call you?</p>
          </div>
        ) : (
          <p className={styles.Hint}>
            One last thing — what should we call you?
          </p>
        )}

        {error && <div className={styles.Error}>{error}</div>}

        <input
          className={styles.Input}
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoFocus
          disabled={nameLoading}
        />
        <input
          className={styles.Input}
          type="text"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleNameSubmit()}
          disabled={nameLoading}
        />
        <button
          className={styles.Btn}
          onClick={handleNameSubmit}
          disabled={nameLoading || !firstName.trim() || !lastName.trim()}
          type="button"
        >
          {nameLoading ? "Saving…" : "Let's go!"}
        </button>
      </>
    );
  }

  // ── Auth step ──────────────────────────────────────────────────────────────

  // Confirming and resending both lock the code screen.
  const otpBusy = loading || sending;

  const headerTitle =
    method === "phone" && otpSent ? "Check your phone" : "Welcome!";
  const headerSubtitle =
    method === "phone" && otpSent
      ? "Enter the 6-digit code we texted you."
      : "Sign in or create an account.";

  return (
    <>
      {showHeader && (
        <div className={styles.Header}>
          <h2 className={styles.HeaderTitle}>{headerTitle}</h2>
          <p className={styles.HeaderSubtitle}>{headerSubtitle}</p>
        </div>
      )}

      {!otpSent && (
        <div className={styles.Toggle}>
          <button
            className={`${styles.ToggleBtn} ${method === "phone" ? styles.ToggleBtnActive : ""}`}
            onClick={() => switchMethod("phone")}
            type="button"
          >
            <PhoneIcon fontSize="small" /> Phone
          </button>
          <button
            className={`${styles.ToggleBtn} ${method === "email" ? styles.ToggleBtnActive : ""}`}
            onClick={() => switchMethod("email")}
            type="button"
          >
            <MailIcon fontSize="small" /> Email
          </button>
        </div>
      )}

      {notice && <div className={styles.Notice}>{notice}</div>}
      {error && <div className={styles.Error}>{error}</div>}

      {/* Phone tab */}
      {method === "phone" &&
        (!otpSent ? (
          <>
            <PhoneInput
              onChange={setFullPhone}
              disabled={sending}
              onEnter={handleSendCode}
              defaultDialCode={defaultDialCode}
              // Seeded from live state, not the prop, so a number corrected
              // before sending survives the "Change" round-trip.
              defaultValue={fullPhone}
            />
            <button
              className={styles.Btn}
              onClick={handleSendCode}
              disabled={sending || !fullPhone.trim()}
              type="button"
            >
              {sending ? "Sending…" : "Send code"}
            </button>
          </>
        ) : (
          <>
            <p className={styles.Hint}>
              Code sent to {fullPhone}.{" "}
              <button
                type="button"
                className={styles.Link}
                onClick={resetOtp}
                disabled={otpBusy}
              >
                Change
              </button>
            </p>
            <input
              className={`${styles.Input} ${styles.OtpInput}`}
              // text + numeric keypad, not type="number": one-time-code
              // autofill (iOS reads the SMS) targets text inputs.
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              disabled={otpBusy}
              autoFocus
            />
            <button
              className={styles.Btn}
              onClick={handleConfirmOtp}
              disabled={otpBusy || otp.length < 4}
              type="button"
            >
              {loading ? "Verifying…" : "Confirm"}
            </button>
            <p className={styles.Hint}>
              Didn't get it?{" "}
              {resendIn > 0 ? (
                `Resend in ${resendIn}s`
              ) : (
                <button
                  type="button"
                  className={styles.Link}
                  onClick={handleSendCode}
                  disabled={otpBusy}
                >
                  {sending ? "Sending…" : "Resend code"}
                </button>
              )}
            </p>
          </>
        ))}

      {/* Email tab */}
      {method === "email" && (
        <>
          <input
            className={styles.Input}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoFocus={!email}
          />
          <input
            className={styles.Input}
            type="password"
            placeholder="Password (at least 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
          />
          {mode === "signin" && (
            <Link
              to="/forgot-password"
              className={styles.ForgotLink}
              tabIndex={-1}
            >
              Forgot password?
            </Link>
          )}
          <button
            className={styles.Btn}
            onClick={handleEmailSubmit}
            disabled={loading || !email.trim() || !password}
            type="button"
          >
            {loading
              ? "Continuing…"
              : mode === "signup"
                ? "Sign me up!"
                : mode === "auto"
                  ? "Continue"
                  : "Sign in"}
          </button>
        </>
      )}

      {showFooter && !otpSent && (
        <p className={styles.Footer}>
          By continuing you agree to our{" "}
          <a
            href="https://sally.app/media/docs/termsandconditions.html"
            target="_blank"
            rel="noreferrer"
          >
            Terms
          </a>{" "}
          &{" "}
          <a
            href="https://sally.app/media/docs/privacypolicy.html"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      )}

      {!otpSent && below}

      <div ref={recaptchaContainerRef} style={{ display: "none" }} />
    </>
  );
};

export default AuthForm;

// ── Error messages ────────────────────────────────────────────────────────────
// https://firebase.google.com/docs/reference/js/auth#autherrorcodes

const PHONE_SEND_ERRORS: Record<string, string> = {
  "auth/invalid-phone-number":
    "That phone number doesn't look right. Try including the country code.",
  "auth/missing-phone-number": "Please enter a phone number.",
  "auth/quota-exceeded": "SMS quota exceeded. Please try again later.",
  "auth/user-disabled":
    "This account has been disabled. Please contact support.",
  "auth/too-many-requests":
    "Too many attempts. Give it a minute and try again!",
  "auth/captcha-check-failed":
    "reCAPTCHA verification failed. Please try again.",
  "auth/network-request-failed":
    "Network error. Check your connection and try again.",
};

const PHONE_CONFIRM_ERRORS: Record<string, string> = {
  "auth/invalid-verification-code": "That code doesn't look right. Try again.",
  "auth/code-expired": "That code has expired. Please request a new one.",
  "auth/missing-verification-code": "Please enter the verification code.",
  "auth/too-many-requests": "Too many attempts. Give it a minute!",
  "auth/network-request-failed":
    "Network error. Check your connection and try again.",
};

const EMAIL_ERRORS: Record<string, string> = {
  "auth/user-not-found": "Email or password is incorrect. Try again!",
  "auth/wrong-password": "Email or password is incorrect. Try again!",
  "auth/invalid-credential": "Email or password is incorrect. Try again!",
  "auth/email-already-in-use":
    "That email is already registered. Try signing in instead.",
  "auth/invalid-email": "That doesn't look like a valid email.",
  "auth/weak-password":
    "Please choose a stronger password (at least 6 characters).",
  "auth/user-disabled":
    "This account has been disabled. Please contact support.",
  "auth/too-many-requests": "Too many attempts. Give it a minute!",
  "auth/network-request-failed":
    "Network error. Check your connection and try again.",
  "auth/operation-not-allowed":
    "Email sign-in is not enabled. Please contact support.",
};

function authError(
  map: Record<string, string>,
  code: string | undefined,
  fallback: string,
): string {
  if (code && map[code]) return map[code];
  return `${fallback}${code ? ` (${code})` : ""}`;
}
